import { ref } from 'vue'
import store from '@/store/index'

const API_BASE = store.state.apiUrl

export function useOdooApi() {
  const loading = ref(false)
  const error = ref(null)

  const request = async (method, path, body = null) => {
    const url = `${API_BASE}${path}`
    const options = {
      method,
      headers: { 'Content-Type': 'application/json' }
    }
    if (body) {
      options.body = JSON.stringify(body)
    }
    const res = await fetch(url, options)
    const data = await res.json()
    if (!res.ok) {
      throw new Error(data.detail || `Request failed with status ${res.status}`)
    }
    return data
  }

  // 1. Health Check
  const healthStatus = ref(null)
  const checkHealth = async () => {
    try {
      healthStatus.value = await request('GET', '/odoo/health')
    } catch (e) {
      healthStatus.value = { status: 'error', error: e.message }
    }
    return healthStatus.value
  }

  // 2. List Companies
  const companies = ref([])
  const fetchCompanies = async () => {
    try {
      companies.value = await request('GET', '/odoo/companies')
    } catch (e) {
      error.value = e.message
    }
    return companies.value
  }

  // 3. Fetch Vendors from Odoo (live query)
  const fetchVendors = async (companyId = null, limit = null, offset = null) => {
    const params = new URLSearchParams()
    if (companyId) params.append('company_id', companyId)
    if (limit) params.append('limit', limit)
    if (offset) params.append('offset', offset)
    const qs = params.toString()
    return await request('GET', `/odoo/vendors${qs ? '?' + qs : ''}`)
  }

  // 4. Sync Odoo Contacts to Firebase
  const syncContacts = async (companyId = null) => {
    const params = companyId ? `?company_id=${companyId}` : ''
    return await request('POST', `/odoo/sync${params}`)
  }

  // 5. Get Synced Contacts from Firebase
  const contacts = ref([])
  const fetchContacts = async () => {
    try {
      loading.value = true
      contacts.value = await request('GET', '/odoo/contacts')
    } catch (e) {
      error.value = e.message
    } finally {
      loading.value = false
    }
    return contacts.value
  }

  // 6. Get Unmatched Contacts
  const unmatchedContacts = ref([])
  const fetchUnmatchedContacts = async () => {
    try {
      loading.value = true
      unmatchedContacts.value = await request('GET', '/odoo/unmatched-contacts')
    } catch (e) {
      error.value = e.message
    } finally {
      loading.value = false
    }
    return unmatchedContacts.value
  }

  // 7. Link Odoo Contact to Existing Supplier (optionally at location level)
  const linkSupplier = async (odooContactId, supplierId, locationId = null) => {
    const body = {
      odoo_contact_id: odooContactId,
      supplier_id: supplierId
    }
    if (locationId != null) {
      body.location_id = locationId
    }
    return await request('POST', '/odoo/link-supplier', body)
  }

  // 8. Unlink Odoo Contact from Supplier
  const unlinkSupplier = async (odooContactId, supplierId, locationId = null) => {
    const body = {
      odoo_contact_id: odooContactId,
      supplier_id: supplierId
    }
    if (locationId != null) {
      body.location_id = locationId
    }
    return await request('POST', '/odoo/unlink-supplier', body)
  }

  // 9. Create New Supplier from Odoo Contact
  const createSupplier = async (odooContactId, operationalName, siteId) => {
    return await request('POST', '/odoo/create-supplier', {
      odoo_contact_id: odooContactId,
      operational_name: operationalName,
      site_id: siteId
    })
  }

  return {
    loading,
    error,
    healthStatus,
    companies,
    contacts,
    unmatchedContacts,
    checkHealth,
    fetchCompanies,
    fetchVendors,
    syncContacts,
    fetchContacts,
    fetchUnmatchedContacts,
    linkSupplier,
    unlinkSupplier,
    createSupplier
  }
}
